import React, { useState, useEffect, useMemo, useRef } from 'react';

const API = 'https://talku.online';
const SF = "'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const fmtMoney = (v) => (v || v === 0) ? `$${Number(v).toLocaleString('es-CO')}` : '—';
const fmtInt = (v) => (v || v === 0) ? Number(v).toLocaleString('es-CO') : '—';
const fmtAgo = (ts) => {
  if (!ts) return '';
  const diff = Math.floor((Date.now() - Number(ts)) / 1000);
  if (diff < 5) return 'ahora';
  if (diff < 60) return `hace ${diff}s`;
  if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
  return `hace ${Math.floor(diff / 3600)}h`;
};
// Descripción visual por tipo de evento SSE
const EVENT_META = {
  'order.created': { dot: '#6D5AE0', txt: (e) => `Nuevo pedido ${e.order_number || ''}`, sub: (e) => e.total ? fmtMoney(e.total) : '' },
  'order.confirmed': { dot: '#2FA468', txt: (e) => `Pedido confirmado ${e.order_number || ''}`, sub: () => '' },
  'order.dispatched': { dot: '#64748B', txt: (e) => `Guía generada ${e.order_number || ''}`, sub: (e) => e.carrier || '' },
  'order.delivered': { dot: '#2FA468', txt: (e) => `Pedido entregado ${e.order_number || ''}`, sub: () => '' },
  'order.novelty': { dot: '#C58A24', txt: (e) => `Novedad ${e.order_number || ''}`, sub: (e) => e.desc || '' },
  'conversation.escalated': { dot: '#C58A24', txt: () => 'Escalado a asesor', sub: (e) => e.contact_name || e.reason || '' },
  'conversation.new': { dot: '#6D5AE0', txt: () => 'Nueva conversación', sub: (e) => e.contact_name || '' },
  'message.inbound': { dot: '#64748B', txt: (e) => e.contact_name || 'Mensaje nuevo', sub: (e) => e.preview || '' },
  'message.failed': { dot: '#D94A4A', txt: () => 'Envío fallido', sub: (e) => e.error || '' },
};
// Iconos semánticos por tipo de alerta (diferenciación visual)
const ALERT_ICONS = {
  escalados: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/><polyline points="17 3 20 6 17 9"/></svg>,
  sinresp: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>,
  novedades: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12" y2="17"/></svg>,
  fallidos: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>,
};
// Logos reales de métodos de pago existentes en el proyecto (frontend/payment-logos/).
// Métodos sin logo → icono neutro en el render.
const PAY_LOGOS = {
  addi: '/payment-logos/addi.png',
  sistecredito: '/payment-logos/sistecredito.png',
};

// ── prefers-reduced-motion ──
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduced(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
  }, []);
  return reduced;
}

// ── count-up numérico suave (tween desde valor previo → nuevo) ──
function useCountUp(target, reduced) {
  const [val, setVal] = useState(target || 0);
  const prev = useRef(target || 0);
  useEffect(() => {
    const to = Number(target) || 0;
    if (reduced) { setVal(to); prev.current = to; return; }
    const from = prev.current; const dur = 400; const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(from + (to - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick); else prev.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, reduced]);
  return val;
}

function KpiCard({ label, target, money, trend, trendText, loading, error, reduced, delay, icon, iconTone }) {
  const n = useCountUp(loading || error ? 0 : target, reduced);
  const display = loading ? '—' : error ? '—' : (money ? fmtMoney(Math.round(n)) : fmtInt(Math.round(n)));
  return (
    <div className="db-card db-kpi db-anim" style={{ animationDelay: `${delay}ms` }}>
      {icon && <span className={`db-kpi-icon ${iconTone || ''}`}>{icon}</span>}
      <div className="db-kpi-label">{label}</div>
      <div className="db-kpi-value">{loading ? <span className="db-skel db-skel-num" /> : display}</div>
      <div className={`db-kpi-trend ${trend || ''}`}>
        {trend === 'up' && <span className="db-tri">▲</span>}
        {trend === 'down' && <span className="db-tri">▼</span>}
        {trendText || ' '}
      </div>
    </div>
  );
}

// Gráfico de líneas analítico (SVG, sin dependencias): Ingresos (lavanda) + Pedidos (gris),
// área muy sutil, grid casi invisible, puntos y tooltip solo al hover.
function LineChart({ data }) {
  const [hi, setHi] = useState(null);
  const n = data.length;
  if (n === 0) return <div className="db-empty">Sin datos en el rango seleccionado.</div>;
  const W = 640, H = 210, PX = 6, PY = 14;
  const maxC = Math.max(...data.map(d => d.count), 1);
  const maxR = Math.max(...data.map(d => d.revenue), 1);
  const xAt = i => (n === 1 ? W / 2 : PX + (i * (W - 2 * PX)) / (n - 1));
  const yC = v => H - PY - (v / maxC) * (H - 2 * PY);
  const yR = v => H - PY - (v / maxR) * (H - 2 * PY);
  // Curva suave (Catmull-Rom → bézier cúbica)
  const smooth = (yf) => {
    const pts = data.map((d, i) => [xAt(i), yf(d)]);
    if (pts.length === 1) return `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`;
    let dp = `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`;
    const t = 0.18;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
      const c1x = p1[0] + (p2[0] - p0[0]) * t, c1y = p1[1] + (p2[1] - p0[1]) * t;
      const c2x = p2[0] - (p3[0] - p1[0]) * t, c2y = p2[1] - (p3[1] - p1[1]) * t;
      dp += ` C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)}`;
    }
    return dp;
  };
  const pathR = smooth(d => yR(d.revenue));
  const areaR = `${pathR} L${xAt(n - 1).toFixed(1)},${H - PY} L${xAt(0).toFixed(1)},${H - PY} Z`;
  const pathC = smooth(d => yC(d.count));
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const rel = ((e.clientX - r.left) / r.width) * W;
    let idx = Math.round((rel - PX) / ((W - 2 * PX) / Math.max(1, n - 1)));
    setHi(Math.max(0, Math.min(n - 1, idx)));
  };
  const step = Math.max(1, Math.ceil(n / 8));
  return (
    <div className="db-line-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="db-line-svg" onMouseMove={onMove} onMouseLeave={() => setHi(null)}>
        {[0.25, 0.5, 0.75].map((g, i) => <line key={i} x1="0" x2={W} y1={H * g} y2={H * g} className="db-line-grid" />)}
        <path d={areaR} className="db-area" />
        <path d={pathR} className="db-line db-line-rev" />
        <path d={pathC} className="db-line db-line-ord" />
        {hi != null && <line x1={xAt(hi)} x2={xAt(hi)} y1="0" y2={H} className="db-line-cursor" />}
        {hi != null && <circle cx={xAt(hi)} cy={yR(data[hi].revenue)} r="3.5" className="db-dot-rev" />}
        {hi != null && <circle cx={xAt(hi)} cy={yC(data[hi].count)} r="3.5" className="db-dot-ord" />}
      </svg>
      {hi != null && (
        <div className="db-tip" style={{ left: `${Math.max(8, Math.min(92, (xAt(hi) / W) * 100))}%` }}>
          <div className="db-tip-date">{data[hi].label}</div>
          <div className="db-tip-row"><span className="db-tip-k rev" />Ingresos: <b>{fmtMoney(data[hi].revenue)}</b></div>
          <div className="db-tip-row"><span className="db-tip-k ord" />Pedidos: <b>{fmtInt(data[hi].count)}</b></div>
        </div>
      )}
      <div className="db-xlabels">
        {data.map((d, i) => <span key={i} style={{ opacity: (n <= 12 || i % step === 0) ? 1 : 0 }}>{d.label}</span>)}
      </div>
      <div className="db-legend">
        <span><span className="db-lg-dot rev" />Ingresos</span>
        <span><span className="db-lg-dot ord" />Pedidos</span>
      </div>
    </div>
  );
}

export default function Dashboard({ user, onNav }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [period, setPeriod] = useState('Semana');     // Día | Semana | Mes | Personalizado
  const [range, setRange] = useState(() => {
    const iso = d => d.toISOString().slice(0, 10);
    const to = new Date(); const from = new Date(); from.setDate(from.getDate() - 29);
    return { from: iso(from), to: iso(to) };
  });
  const [series, setSeries] = useState([]);
  // Estado "en vivo": feed de eventos + deltas incrementales (el polling los reconcilia a 0)
  const [live, setLive] = useState({ events: [], dOrders: 0, dRevenue: 0, dPipe: {}, dEsc: 0, dNov: 0 });
  const reduced = usePrefersReducedMotion();

  const now = new Date();
  const greeting = now.getHours() < 12 ? 'Buenos días' : now.getHours() < 18 ? 'Buenas tardes' : 'Buenas noches';
  const userName = user?.name || user?.email?.split('@')[0] || '';

  const load = async () => {
    try {
      const token = user?.token || localStorage.getItem('talku_token') || '';
      const res = await fetch(`${API}/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('bad');
      setStats(await res.json());
      setError(false);
      // reconciliación: al llegar datos completos, se anulan los deltas incrementales
      setLive(l => ({ ...l, dOrders: 0, dRevenue: 0, dPipe: {}, dEsc: 0, dNov: 0 }));
    } catch {
      setError(true);
    }
    setLoading(false);
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [user]); // eslint-disable-line

  // ── SSE: tiempo real (aditivo; el polling sigue como fallback/reconciliación) ──
  useEffect(() => {
    const token = user?.token || localStorage.getItem('talku_token') || '';
    if (!token || typeof EventSource === 'undefined') return;
    let es = null, closed = false, backoff = 1000, timer = null;
    const bump = (dPipe, k, n = 1) => ({ ...dPipe, [k]: (dPipe[k] || 0) + n });
    const onEvent = (ev) => setLive(l => {
      const events = [ev, ...l.events].slice(0, 20);
      let { dOrders, dRevenue, dPipe, dEsc, dNov } = l;
      switch (ev.type) {
        case 'order.created': dOrders += 1; dRevenue += Number(ev.total) || 0; dPipe = bump(dPipe, 'nuevos'); break;
        case 'order.confirmed': dPipe = bump(dPipe, 'confirmados'); dPipe = bump(dPipe, 'nuevos', -1); break;
        case 'order.dispatched': dPipe = bump(dPipe, 'despachados'); break;
        case 'order.delivered': dPipe = bump(dPipe, 'entregados'); break;
        case 'order.novelty': dPipe = bump(dPipe, 'novedades'); dNov += 1; break;
        case 'conversation.escalated': dEsc += 1; break;
        default: break; // message.inbound / conversation.new / message.failed → solo feed
      }
      return { events, dOrders, dRevenue, dPipe, dEsc, dNov };
    });
    const retry = () => {
      if (!closed) { timer = setTimeout(connect, Math.min(backoff, 30000)); backoff = Math.min(backoff * 2, 30000); }
    };
    const connect = async () => {
      if (closed) return;
      let ticket = '';
      try {
        const r = await fetch(`${API}/dashboard/stream-ticket`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) ticket = (await r.json()).ticket || '';
      } catch { /* ignore */ }
      if (!ticket || closed) { retry(); return; }  // sin ticket → backoff (el polling sigue)
      es = new EventSource(`${API}/dashboard/stream?ticket=${encodeURIComponent(ticket)}`);
      es.onopen = () => { backoff = 1000; };
      es.onmessage = (e) => { try { onEvent(JSON.parse(e.data)); } catch { /* ignore */ } };
      es.onerror = () => { if (es) es.close(); retry(); };
    };
    connect();
    return () => { closed = true; if (timer) clearTimeout(timer); if (es) es.close(); };
  }, [user]); // eslint-disable-line

  const s = stats || {};
  const orders = s.orders || {};
  const convs = s.conversations || {};

  // ── Serie por rango arbitrario (solo periodo Personalizado) ──
  useEffect(() => {
    if (period !== 'Personalizado' || !range.from || !range.to) return;
    let cancel = false;
    const token = user?.token || localStorage.getItem('talku_token') || '';
    fetch(`${API}/dashboard/series?frm=${range.from}&to=${range.to}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : { series: [] }))
      .then(d => { if (!cancel) setSeries(d.series || []); })
      .catch(() => { if (!cancel) setSeries([]); });
    return () => { cancel = true; };
  }, [period, range.from, range.to, user]); // eslint-disable-line

  // ── Gráfico principal: dataset según periodo ──
  const chartData = useMemo(() => {
    if (period === 'Personalizado') {
      return (series || []).map(p => ({ label: (p.date || '').slice(5), count: p.orders || 0, revenue: p.revenue || 0 }));
    }
    const src = period === 'Día' ? (s.order_bars_daily || [])
      : period === 'Mes' ? (s.order_bars_monthly || [])
      : (s.order_bars_weekly || s.order_bars || []);
    return src.map(b => ({ label: b.label, count: b.count ?? b.v ?? 0, revenue: b.revenue ?? 0, is_today: b.is_today }));
  }, [period, series, s.order_bars_daily, s.order_bars_weekly, s.order_bars_monthly, s.order_bars]);

  const totalCount = chartData.reduce((a, b) => a + b.count, 0);
  const totalRev = chartData.reduce((a, b) => a + b.revenue, 0);

  // ── KPI trend hoy vs ayer (pedidos e ingresos, datos reales) ──
  const mkTrend = (a, b) => {
    if (typeof a !== 'number' || typeof b !== 'number' || b <= 0) return [null, null];
    const d = Math.round(((a - b) / b) * 100);
    return [d > 0 ? 'up' : d < 0 ? 'down' : 'flat', `${d > 0 ? '+' : ''}${d}% vs ayer`];
  };
  const [pedTrend, pedText] = mkTrend(orders.today, orders.yesterday);
  const [venTrend, venText] = mkTrend(orders.revenue_today, orders.revenue_yesterday);

  // ── Pipeline (últimos 30 días) — datos reales de s.pipeline + deltas en vivo ──
  const pl = s.pipeline || {};
  const pv = (k, base) => (typeof base === 'number' ? Math.max(0, base + (live.dPipe[k] || 0)) : base);
  const pipeline = [
    { key: 'nuevos', name: 'Nuevos', value: pv('nuevos', pl.nuevos), tone: 'lav' },
    { key: 'confirmados', name: 'Confirmados', value: pv('confirmados', pl.confirmados), tone: 'green' },
    { key: 'despachados', name: 'Despachados', value: pv('despachados', pl.despachados), tone: 'blue' },
    { key: 'entregados', name: 'Entregados', value: pv('entregados', pl.entregados), tone: 'green' },
    { key: 'cancelados', name: 'Cancelados', value: pv('cancelados', pl.cancelados), tone: 'red' },
    { key: 'novedades', name: 'Novedades', value: pv('novedades', pl.novedades), tone: 'amber' },
  ];
  const pipeMax = Math.max(...pipeline.map(p => (typeof p.value === 'number' ? p.value : 0)), 1);

  // ── Alertas ── (envíos fallidos requiere persistencia → placeholder)
  const escalados = typeof convs.urgent === 'number' ? convs.urgent + live.dEsc : convs.urgent;
  const novedadesActivas = typeof pl.novedades === 'number' ? Math.max(0, pl.novedades + live.dNov) : pl.novedades;
  const alerts = [
    { key: 'escalados', label: 'Escalados', value: escalados, tone: 'red', nav: 'Conversaciones' },
    { key: 'sinresp', label: 'Sin responder', value: convs.unread, tone: 'amber', nav: 'Conversaciones' },
    { key: 'novedades', label: 'Novedades', value: novedadesActivas, tone: 'amber', nav: null },
    { key: 'fallidos', label: 'Envíos fallidos', value: null, tone: 'red', nav: null },
  ];

  const recent = (s.recent_conversations || []).slice(0, 6);
  const daySales = (s.order_bars_daily || []);
  const crm = s.crm || {};
  const topProducts = s.top_products || [];
  const payMethods = s.payment_methods || [];
  const PM_LABELS = { cod: 'Contra entrega', addi: 'Addi', sistecredito: 'Sistecrédito', wompi: 'Wompi', mercadopago: 'MercadoPago', openpay: 'Openpay', nequi: 'Nequi', daviplata: 'Daviplata', otro: 'Otro' };

  const statusDot = (st) => st === 'escalated' ? '#D94A4A' : st === 'waiting_agent' ? '#C58A24' : st === 'resolved' ? '#9ca3af' : '#2FA468';

  return (
    <div className="db-root">
      <style>{`
        .db-root { flex:1; overflow-y:auto; background:#F5F5F3; font-family:${SF}; padding:40px; }
        .db-inner { max-width:1400px; margin:0 auto; }
        .db-root::-webkit-scrollbar{width:6px;} .db-root::-webkit-scrollbar-thumb{background:#d8d8d4;border-radius:3px;}

        .db-card { background:#fff; border-radius:20px; padding:24px; box-shadow:0 1px 2px rgba(0,0,0,.04); transition:box-shadow .16s ease, transform .16s ease; }
        .db-card.hoverable:hover { box-shadow:0 6px 20px rgba(0,0,0,.06); transform:translateY(-2px); }

        /* entrada */
        @keyframes dbIn { from { opacity:0; transform:translateY(8px);} to { opacity:1; transform:translateY(0);} }
        .db-anim { animation:dbIn .2s ease-out both; }

        /* header */
        .db-header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:24px; }
        .db-h-greet { font-size:26px; font-weight:640; color:#0F0F10; letter-spacing:-.4px; margin:0; }
        .db-h-sub { font-size:13px; color:#6B7280; margin:6px 0 0; }
        .db-pill-group { display:inline-flex; background:#fff; border-radius:12px; padding:4px; box-shadow:0 1px 2px rgba(0,0,0,.04); }
        .db-pill { border:none; background:transparent; font-family:${SF}; font-size:12.5px; font-weight:600; color:#6B7280; padding:6px 12px; border-radius:8px; cursor:pointer; transition:all .15s; }
        .db-pill.active { background:#6D5AE0; color:#fff; }
        .db-controls { display:flex; align-items:center; gap:12px; flex-wrap:wrap; justify-content:flex-end; }
        .db-range { display:inline-flex; align-items:center; gap:6px; background:#fff; border:1px solid #ECECEC; border-radius:12px; padding:3px 8px; box-shadow:0 1px 2px rgba(0,0,0,.04); }
        .db-date { border:none; outline:none; padding:5px 4px; font-size:12px; font-weight:500; font-family:${SF}; color:#374151; background:transparent; }
        .db-date::-webkit-calendar-picker-indicator { opacity:.45; cursor:pointer; }
        .db-range-arrow { color:#9CA3AF; font-size:12px; }

        /* grid */
        .db-grid { display:grid; grid-template-columns:repeat(12,1fr); gap:20px; align-items:start; }
        .db-kpirow { display:grid; grid-template-columns:repeat(4,1fr); gap:20px; margin-bottom:20px; }

        /* KPI */
        .db-kpi { position:relative; }
        .db-kpi-icon { position:absolute; top:20px; right:20px; width:34px; height:34px; border-radius:11px; display:flex; align-items:center; justify-content:center; line-height:0; background:#F2F2F0; color:#9CA3AF; }
        .db-kpi-icon.lav { background:#ECE9FB; color:#6D5AE0; }
        .db-kpi-icon.blue { background:#E7EDF8; color:#4F79C7; }
        .db-kpi-icon.green { background:#E4F3EA; color:#2FA468; }
        .db-kpi-icon.amber { background:#F7EFDC; color:#C58A24; }
        .db-kpi-label { font-size:11px; font-weight:600; letter-spacing:.05em; text-transform:uppercase; color:#9CA3AF; }
        .db-kpi-value { font-size:36px; font-weight:680; color:#0F0F10; letter-spacing:-.5px; margin:10px 0 6px; font-variant-numeric:tabular-nums; }
        .db-kpi-trend { font-size:12px; color:#9CA3AF; display:flex; align-items:center; gap:5px; min-height:16px; }
        .db-kpi-trend.up { color:#2FA468; } .db-kpi-trend.down { color:#D94A4A; }
        .db-tri { font-size:9px; }

        /* card titles */
        .db-ct { display:flex; justify-content:space-between; align-items:center; margin-bottom:18px; }
        .db-ct h3 { font-size:15px; font-weight:600; color:#0F0F10; margin:0; }
        .db-ct-note { font-size:11px; color:#9CA3AF; }

        /* chart */
        .db-chart-wrap { position:relative; height:230px; display:flex; }
        .db-yaxis { display:flex; flex-direction:column; justify-content:space-between; width:44px; font-size:10px; color:#C4C4C4; text-align:right; padding-right:8px; }
        .db-plot { flex:1; position:relative; display:flex; align-items:flex-end; gap:2%; border-left:1px solid #F0F0EE; padding-left:10px; }
        .db-guide { position:absolute; left:0; right:0; border-top:1px solid #F4F4F2; }
        .db-bar-col { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:flex-end; height:100%; position:relative; z-index:1; }
        .db-bar { width:60%; max-width:34px; background:#0F0F10; border-radius:7px 7px 0 0; transition:height .3s cubic-bezier(.4,0,.2,1); }
        .db-bar.today { background:#6D5AE0; }
        .db-bar-lbl { font-size:10.5px; color:#9CA3AF; margin-top:8px; }
        .db-chart-foot { display:flex; gap:32px; margin-top:12px; padding-top:14px; border-top:1px solid #F0F0EE; }
        .db-foot-lbl { font-size:11px; color:#9CA3AF; } .db-foot-val { font-size:18px; font-weight:640; color:#0F0F10; margin-top:2px; }
        /* Gráfico de líneas */
        .db-line-wrap { position:relative; }
        .db-line-svg { width:100%; height:272px; display:block; }
        .db-line-grid { stroke:#F6F6F4; stroke-width:1; }
        .db-area { fill:#6D5AE0; opacity:.11; }
        .db-line { fill:none; stroke-width:2; vector-effect:non-scaling-stroke; }
        .db-line-rev { stroke:#6D5AE0; }
        .db-line-ord { stroke:#4F79C7; }
        .db-line-cursor { stroke:#DADADA; stroke-width:1; vector-effect:non-scaling-stroke; }
        .db-dot-rev { fill:#6D5AE0; } .db-dot-ord { fill:#4F79C7; }
        .db-tip { position:absolute; top:2px; transform:translateX(-50%); background:#0F0F10; color:#fff; border-radius:10px; padding:8px 10px; font-size:11px; line-height:1.5; pointer-events:none; white-space:nowrap; box-shadow:0 6px 18px rgba(0,0,0,.18); z-index:3; }
        .db-tip-date { font-weight:600; margin-bottom:3px; }
        .db-tip-row { display:flex; align-items:center; gap:5px; opacity:.92; }
        .db-tip-k { width:7px; height:7px; border-radius:50%; display:inline-block; }
        .db-tip-k.rev { background:#6D5AE0; } .db-tip-k.ord { background:#4F79C7; }
        .db-xlabels { display:flex; margin-top:8px; }
        .db-xlabels span { flex:1; text-align:center; font-size:10px; color:#9CA3AF; overflow:hidden; white-space:nowrap; }
        .db-legend { display:flex; gap:16px; margin-top:6px; font-size:11px; color:#6B7280; }
        .db-lg-dot { display:inline-block; width:8px; height:8px; border-radius:50%; margin-right:5px; }
        .db-lg-dot.rev { background:#6D5AE0; } .db-lg-dot.ord { background:#4F79C7; }

        /* pipeline / lists */
        .db-row { display:flex; align-items:center; gap:12px; padding:13px 0; }
        .db-row + .db-row { border-top:1px solid #F4F4F2; }
        .db-row-name { font-size:13px; color:#374151; width:96px; flex-shrink:0; }
        .db-track { flex:1; height:11px; background:#F1F1EF; border-radius:6px; overflow:hidden; }
        .db-fill { height:100%; border-radius:6px; background:#94A3B8; transition:width .3s ease; }
        .db-fill.lav{background:#6D5AE0;} .db-fill.green{background:#2FA468;} .db-fill.slate{background:#64748B;} .db-fill.blue{background:#4F79C7;} .db-fill.amber{background:#C58A24;} .db-fill.red{background:#D94A4A;}
        .db-row-val { font-size:15px; font-weight:660; color:#0F0F10; width:52px; text-align:right; font-variant-numeric:tabular-nums; }
        .db-row-val.muted { color:#C4C4C4; font-weight:500; }
        .db-row-val.lav { color:#6D5AE0; }
        .db-thumb { position:relative; width:28px; height:28px; border-radius:8px; background:#F2F2F0; color:#9CA3AF; font-size:12px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; overflow:hidden; }
        .db-thumb img { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; }
        .db-pay-logo { width:28px; height:28px; border-radius:8px; background:#F7F7F5; color:#9CA3AF; display:flex; align-items:center; justify-content:center; flex-shrink:0; overflow:hidden; }
        .db-pay-logo img { width:100%; height:100%; object-fit:contain; }
        .db-ia-tag { font-size:10px; font-weight:700; letter-spacing:.05em; color:#6D5AE0; background:#ECE9FB; padding:3px 8px; border-radius:6px; }
        .db-ia-dot { display:inline-block; width:7px; height:7px; border-radius:50%; background:#6D5AE0; margin-right:7px; vertical-align:middle; }

        /* alerts */
        .db-alert { display:flex; align-items:center; justify-content:space-between; padding:13px 10px; margin:0 -10px; border-radius:12px; cursor:default; transition:background .15s; }
        .db-alert.clickable { cursor:pointer; }
        .db-alert.clickable:hover { background:#FAFAF9; }
        .db-alert.active.amber { background:#F7EFDC; } .db-alert.active.red { background:#FBE4E4; }
        .db-alert + .db-alert { border-top:1px solid #F4F4F2; }
        .db-alert-l { display:flex; align-items:center; gap:10px; }
        .db-alert-ic { color:#C4C4C4; line-height:0; display:flex; }
        .db-alert-ic.amber { color:#C58A24; } .db-alert-ic.red { color:#D94A4A; }
        .db-alert-r { display:flex; align-items:center; gap:8px; }
        .db-alert-n { font-size:19px; font-weight:680; color:#9CA3AF; font-variant-numeric:tabular-nums; min-width:26px; text-align:right; }
        .db-alert.active .db-alert-n { font-size:23px; }
        .db-alert-n.amber { color:#C58A24; } .db-alert-n.red { color:#D94A4A; }
        .db-alert-lbl { font-size:13px; color:#374151; }
        .db-chev { color:#D4D4D4; }

        /* activity */
        .db-act { display:flex; gap:11px; padding:11px 0; align-items:flex-start; }
        .db-act + .db-act { border-top:1px solid #F4F4F2; }
        .db-act-dot { width:8px; height:8px; border-radius:50%; margin-top:5px; flex-shrink:0; }
        .db-act-dot.live { animation:dbPulse 2.2s ease-out infinite; }
        @keyframes dbPulse { 0%{box-shadow:0 0 0 0 rgba(109,90,224,.30);} 70%{box-shadow:0 0 0 6px rgba(109,90,224,0);} 100%{box-shadow:0 0 0 0 rgba(109,90,224,0);} }
        .db-act-txt { font-size:13px; color:#0F0F10; font-weight:500; }
        .db-act-sub { font-size:12px; color:#9CA3AF; margin-top:1px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; max-width:100%; }
        .db-act-in { animation:dbIn .3s ease-out both; }

        /* mini bars (analytics) */
        .db-mini { display:flex; align-items:flex-end; gap:4px; height:90px; }
        .db-mini-col { flex:1; background:#EEEBFB; border-radius:4px 4px 0 0; min-height:2px; transition:height .3s ease; }

        /* states */
        .db-empty { font-size:13px; color:#9CA3AF; text-align:center; padding:28px 0; }
        .db-soon { display:inline-block; font-size:10px; font-weight:600; letter-spacing:.04em; text-transform:uppercase; color:#9CA3AF; background:#F4F4F2; padding:3px 8px; border-radius:6px; }
        .db-skel { display:block; background:#EFEFED; border-radius:8px; }
        .db-skel-num { width:70%; height:34px; }
        .db-skel-line { height:12px; margin:9px 0; } .db-skel-line.s { width:60%; }
        .db-err { background:#FCECEC; color:#D94A4A; font-size:13px; border-radius:12px; padding:12px 16px; display:flex; justify-content:space-between; align-items:center; margin-bottom:20px; }
        .db-retry { background:#D94A4A; color:#fff; border:none; border-radius:8px; padding:6px 14px; font-size:12px; font-weight:600; cursor:pointer; font-family:${SF}; }

        .col8{grid-column:span 8;} .col4{grid-column:span 4;} .col5{grid-column:span 5;} .col3{grid-column:span 3;}

        @media (max-width:1199px){
          .db-root{padding:28px;}
          .db-kpirow{grid-template-columns:repeat(2,1fr);}
          .col8,.col4,.col5,.col3{grid-column:span 12;}
        }
        @media (max-width:640px){
          .db-root{padding:18px;}
          .db-kpirow{grid-template-columns:repeat(2,1fr);gap:12px;}
          .db-kpi-value{font-size:28px;}
          .db-h-greet{font-size:22px;}
        }
        @media (prefers-reduced-motion: reduce){
          .db-anim,.db-act-in,.db-act-dot.live{animation:none;}
          .db-bar,.db-fill,.db-mini-col,.db-card,.db-line{transition:none;}
        }
      `}</style>

      <div className="db-inner">
        {/* HEADER */}
        <div className="db-header">
          <div>
            <h1 className="db-h-greet">{greeting}{userName ? `, ${userName}` : ''}</h1>
            <p className="db-h-sub">
              {loading ? 'Cargando estado del negocio…'
                : error ? 'No se pudo cargar el estado del negocio.'
                : `Hoy: ${fmtInt(orders.today || 0)} pedidos · ${fmtMoney(orders.revenue_today || 0)} · ${fmtInt(convs.active || 0)} conversaciones activas`}
            </p>
          </div>
          <div className="db-controls">
            <div className="db-pill-group">
              {['Día', 'Semana', 'Mes', 'Personalizado'].map(p => (
                <button key={p} className={`db-pill ${period === p ? 'active' : ''}`} onClick={() => setPeriod(p)}>{p}</button>
              ))}
            </div>
            {period === 'Personalizado' && (
              <div className="db-range">
                <input type="date" className="db-date" value={range.from} max={range.to} onChange={e => setRange(r => ({ ...r, from: e.target.value }))} />
                <span className="db-range-arrow">→</span>
                <input type="date" className="db-date" value={range.to} min={range.from} onChange={e => setRange(r => ({ ...r, to: e.target.value }))} />
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="db-err">
            <span>Error al cargar los datos del dashboard.</span>
            <button className="db-retry" onClick={() => { setLoading(true); load(); }}>Reintentar</button>
          </div>
        )}

        {/* KPIs */}
        <div className="db-kpirow">
          <KpiCard label="Ventas hoy" iconTone="lav" target={(orders.revenue_today || 0) + live.dRevenue} money trend={venTrend} trendText={venText} loading={loading} error={error} reduced={reduced} delay={0}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>} />
          <KpiCard label="Pedidos hoy" iconTone="blue" target={(orders.today || 0) + live.dOrders} trend={pedTrend} trendText={pedText} loading={loading} error={error} reduced={reduced} delay={40}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>} />
          <KpiCard label="Conversaciones activas" iconTone="green" target={convs.active} loading={loading} error={error} reduced={reduced} delay={80}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>} />
          <KpiCard label="Ticket promedio" iconTone="amber" target={orders.avg_order} money loading={loading} error={error} reduced={reduced} delay={120}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1z"/><line x1="8" y1="8" x2="16" y2="8"/><line x1="8" y1="12" x2="14" y2="12"/></svg>} />
        </div>

        {/* FILA B: Evolución + Pipeline */}
        <div className="db-grid" style={{ marginBottom: 20 }}>
          <div className="db-card hoverable db-anim col8" style={{ animationDelay: '140ms' }}>
            <div className="db-ct">
              <h3>Evolución</h3>
              <span className="db-ct-note">
                {period === 'Personalizado' ? `${range.from} → ${range.to}` : period}
              </span>
            </div>
            {loading ? (
              <div><div className="db-skel db-skel-line" /><div className="db-skel db-skel-line" /><div className="db-skel db-skel-line s" /></div>
            ) : (
              <>
                <LineChart data={chartData} />
                <div className="db-chart-foot">
                  <div><div className="db-foot-lbl">Total pedidos del rango</div><div className="db-foot-val">{fmtInt(totalCount)}</div></div>
                  <div><div className="db-foot-lbl">Total ingresos del rango</div><div className="db-foot-val">{fmtMoney(totalRev)}</div></div>
                </div>
              </>
            )}
          </div>

          <div className="db-card hoverable db-anim col4" style={{ animationDelay: '180ms' }}>
            <div className="db-ct"><h3>Pipeline de pedidos</h3><span className="db-ct-note">últimos 30 días</span></div>
            {loading ? <div><div className="db-skel db-skel-line" /><div className="db-skel db-skel-line" /><div className="db-skel db-skel-line" /></div> : (
              pipeline.map(p => {
                const has = typeof p.value === 'number';
                return (
                  <div className="db-row" key={p.key}>
                    <span className="db-row-name">{p.name}</span>
                    <span className="db-track">{has && <span className={`db-fill ${p.tone || ''}`} style={{ width: `${(p.value / pipeMax) * 100}%` }} />}</span>
                    <span className={`db-row-val ${has ? '' : 'muted'}`}>{has ? fmtInt(p.value) : '—'}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* FILA C: Actividad + Alertas + IA/CRM */}
        <div className="db-grid" style={{ marginBottom: 20 }}>
          <div className="db-card hoverable db-anim col5" style={{ animationDelay: '220ms' }}>
            <div className="db-ct"><h3>Actividad reciente</h3>{live.events.length > 0 && <span className="db-soon">en vivo</span>}</div>
            {loading ? <div><div className="db-skel db-skel-line" /><div className="db-skel db-skel-line" /></div>
              : live.events.length > 0 ? live.events.map((ev, i) => {
                  const meta = EVENT_META[ev.type] || { dot: '#9ca3af', txt: () => ev.type, sub: () => '' };
                  return (
                    <div className="db-act db-act-in" key={`${ev.ts}-${i}`}>
                      <span className={`db-act-dot ${i === 0 ? 'live' : ''}`} style={{ background: meta.dot, '--dot': meta.dot }} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div className="db-act-txt">{meta.txt(ev)}</div>
                        <div className="db-act-sub">{meta.sub(ev)}</div>
                      </div>
                      <span className="db-act-sub" style={{ flexShrink: 0 }}>{fmtAgo(ev.ts)}</span>
                    </div>
                  );
                })
              : recent.length === 0 ? <div className="db-empty">Sin actividad reciente.</div>
              : recent.map((c, i) => (
                <div className="db-act db-act-in" key={i} style={{ animationDelay: `${i * 40}ms` }}>
                  <span className="db-act-dot" style={{ background: statusDot(c.status) }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="db-act-txt">{c.name || 'Cliente'}</div>
                    <div className="db-act-sub">{c.preview || '—'}</div>
                  </div>
                  {c.unread > 0 && <span className="db-alert-n amber" style={{ fontSize: 13, minWidth: 'auto' }}>{c.unread}</span>}
                </div>
              ))}
          </div>

          <div className="db-card hoverable db-anim col4" style={{ animationDelay: '260ms' }}>
            <div className="db-ct"><h3>Alertas</h3></div>
            {loading ? <div><div className="db-skel db-skel-line" /><div className="db-skel db-skel-line" /></div> : (
              alerts.map(a => {
                const has = typeof a.value === 'number';
                const active = has && a.value > 0;
                const clickable = a.nav && active;
                return (
                  <div className={`db-alert ${clickable ? 'clickable' : ''} ${active ? 'active ' + a.tone : ''}`} key={a.key}
                       onClick={() => clickable && onNav && onNav(a.nav)}>
                    <div className="db-alert-l">
                      <span className={`db-alert-ic ${active ? a.tone : ''}`}>{ALERT_ICONS[a.key]}</span>
                      <span className="db-alert-lbl">{a.label}</span>
                    </div>
                    <div className="db-alert-r">
                      <span className={`db-alert-n ${active ? a.tone : ''}`}>{has ? a.value : '—'}</span>
                      {clickable && <svg className="db-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6" /></svg>}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="db-card hoverable db-anim col3" style={{ animationDelay: '300ms' }}>
            <div className="db-ct"><h3>IA / CRM</h3><span className="db-ia-tag">IA</span></div>
            <div className="db-row"><span className="db-row-name">Nuevos clientes</span><span className="db-track" /><span className={`db-row-val ${typeof crm.new_customers_today === 'number' ? 'lav' : 'muted'}`}>{loading ? '—' : typeof crm.new_customers_today === 'number' ? fmtInt(crm.new_customers_today) : '—'}</span></div>
            <div className="db-row"><span className="db-row-name"><span className="db-ia-dot" />% IA</span><span className="db-track" /><span className="db-row-val muted">—</span></div>
            <div className="db-row"><span className="db-row-name"><span className="db-ia-dot" />Transferencias</span><span className="db-track" /><span className="db-row-val muted">—</span></div>
          </div>
        </div>

        {/* FILA D: Analytics (menor peso) */}
        <div className="db-grid">
          <div className="db-card hoverable db-anim col4" style={{ animationDelay: '340ms' }}>
            <div className="db-ct"><h3>Ventas por hora (hoy)</h3><span className="db-ct-note">ingresos por franja de 4 horas</span></div>
            {loading ? <div className="db-skel db-skel-line" /> : daySales.length === 0 ? <div className="db-empty">Sin ventas hoy.</div> : (
              <div className="db-mini">
                {daySales.map((d, i) => {
                  const mx = Math.max(...daySales.map(x => x.revenue || 0), 1);
                  return <div className="db-mini-col" key={i} title={`${d.label}: ${fmtMoney(d.revenue)}`} style={{ height: `${((d.revenue || 0) / mx) * 100}%` }} />;
                })}
              </div>
            )}
          </div>
          <div className="db-card hoverable db-anim col4" style={{ animationDelay: '380ms' }}>
            <div className="db-ct"><h3>Top productos</h3><span className="db-ct-note">últimos 30 días</span></div>
            {loading ? <div className="db-skel db-skel-line" /> : topProducts.length === 0 ? <div className="db-empty">Sin ventas en el rango.</div> : (
              topProducts.map((p, i) => {
                const mx = Math.max(...topProducts.map(x => x.qty || 0), 1);
                return (
                  <div className="db-row" key={i}>
                    <span className="db-thumb">
                      {(p.title || '?').trim().charAt(0).toUpperCase()}
                      {p.image_url && <img src={p.image_url} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />}
                    </span>
                    <span className="db-row-name" style={{ width: 'auto', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.title}>{p.title}</span>
                    <span className="db-track" style={{ maxWidth: 64 }}><span className="db-fill lav" style={{ width: `${(p.qty / mx) * 100}%` }} /></span>
                    <span className="db-row-val">{fmtInt(p.qty)}</span>
                  </div>
                );
              })
            )}
          </div>
          <div className="db-card hoverable db-anim col4" style={{ animationDelay: '420ms' }}>
            <div className="db-ct"><h3>Métodos de pago</h3><span className="db-ct-note">últimos 30 días</span></div>
            {loading ? <div className="db-skel db-skel-line" /> : payMethods.length === 0 ? <div className="db-empty">Sin datos de pago.</div> : (
              payMethods.map((m, i) => {
                const tot = payMethods.reduce((a, b) => a + (b.count || 0), 0) || 1;
                return (
                  <div className="db-row" key={i}>
                    <span className="db-pay-logo">
                      {PAY_LOGOS[m.method]
                        ? <img src={PAY_LOGOS[m.method]} alt={m.method} />
                        : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="3"/><line x1="2" y1="10" x2="22" y2="10"/></svg>}
                    </span>
                    <span className="db-row-name" style={{ width: 'auto', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{PM_LABELS[m.method] || m.method}</span>
                    <span className="db-track" style={{ maxWidth: 64 }}><span className="db-fill slate" style={{ width: `${(m.count / tot) * 100}%` }} /></span>
                    <span className="db-row-val">{fmtInt(m.count)}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
